import {
  clampSubtitleBoxWidthRatio,
  compensateBurnBoxForBackendTopAlign,
  inferScaleFromFontSizeRatio,
  reverseBurnBoxToDisplayFrame,
  SUBTITLE_PREVIEW_BOX_HEIGHT_FRAC,
} from './autoSubtitleGeometry';

export interface SubtitlePreviewStyleFromBurn {
  relativeCenter: { x: number; y: number };
  fontScale: number;
  boxWidthRatio: number;
}

function parseJsonObject(raw: string): unknown {
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

/** 解析 /video/task/get 下发的 subtitleBurnRequest JSON 字符串 */
export function parseSubtitleBurnRequest(
  raw: string | null | undefined
): Task.SubtitleBurnRequestPayload | null {
  if (!raw || typeof raw !== 'string' || !raw.trim()) return null;
  const parsed = parseJsonObject(raw.trim());
  if (!parsed || typeof parsed !== 'object') return null;

  const burnConfig = (parsed as Task.SubtitleBurnRequestPayload).burnConfig;
  if (!burnConfig || typeof burnConfig !== 'object') return null;
  if (
    typeof burnConfig.fontSize !== 'number' ||
    typeof burnConfig.boxX1 !== 'number' ||
    typeof burnConfig.boxY1 !== 'number' ||
    typeof burnConfig.boxX2 !== 'number' ||
    typeof burnConfig.boxY2 !== 'number' ||
    typeof burnConfig.canvasWidth !== 'number' ||
    typeof burnConfig.canvasHeight !== 'number' ||
    typeof burnConfig.videoWidth !== 'number' ||
    typeof burnConfig.videoHeight !== 'number'
  ) {
    return null;
  }

  return parsed as Task.SubtitleBurnRequestPayload;
}

/**
 * 由 burnConfig（压制用文字块坐标）还原预览层 relativeCenter / fontScale / boxWidthRatio。
 * contentW/H 未传时用 canvas 尺寸（与视频像素 1:1 的 contain 基准一致）。
 */
export function previewStyleFromBurnConfig(
  burnConfig: Task.SubtitleBurnConfig,
  options?: { contentW?: number; contentH?: number }
): SubtitlePreviewStyleFromBurn | null {
  const canvasW = burnConfig.canvasWidth;
  const canvasH = burnConfig.canvasHeight;
  const videoW = burnConfig.videoWidth;
  const videoH = burnConfig.videoHeight;
  if (canvasW <= 0 || canvasH <= 0 || videoW <= 0 || videoH <= 0) return null;

  const contentW = options?.contentW ?? canvasW;
  const contentH = options?.contentH ?? canvasH;
  if (contentW <= 0 || contentH <= 0) return null;

  const scaleY = canvasH / contentH;
  const burnRect = {
    boxX1: burnConfig.boxX1,
    boxY1: burnConfig.boxY1,
    boxX2: burnConfig.boxX2,
    boxY2: burnConfig.boxY2,
  };
  const boxWidthCanvas = Math.max(1, burnRect.boxX2 - burnRect.boxX1);
  const textBlockHeightPx = Math.max(1, (burnRect.boxY2 - burnRect.boxY1) / scaleY);

  const fontSizeRatio = burnConfig.fontSize / videoH;
  const fontScale = inferScaleFromFontSizeRatio(
    fontSizeRatio,
    contentW,
    contentH,
    undefined,
    undefined,
    videoW,
    videoH
  );

  const templateBoxH = contentH * SUBTITLE_PREVIEW_BOX_HEIGHT_FRAC * fontScale;
  const previewBoxHeightPx = Math.max(templateBoxH, textBlockHeightPx);

  const frameRect = reverseBurnBoxToDisplayFrame({
    burnRect,
    previewBoxHeightPx,
    textBlockHeightPx,
    contentH,
    canvasH,
  });

  const frameHCanvas = Math.max(1, Math.round(previewBoxHeightPx * scaleY));
  const relativeCenter = {
    x: Math.max(0, Math.min(1, (frameRect.boxX1 + boxWidthCanvas / 2) / canvasW)),
    y: Math.max(0, Math.min(1, (frameRect.boxY1 + frameHCanvas / 2) / canvasH)),
  };
  const boxWidthRatio = clampSubtitleBoxWidthRatio(boxWidthCanvas / canvasW);

  return { relativeCenter, fontScale, boxWidthRatio };
}

/** 由 subtitleBurnRequest 字符串直接还原预览样式 */
export function previewStyleFromSubtitleBurnRequest(
  raw: string | null | undefined,
  options?: { contentW?: number; contentH?: number }
): SubtitlePreviewStyleFromBurn | null {
  const parsed = parseSubtitleBurnRequest(raw);
  if (!parsed?.burnConfig) return null;
  return previewStyleFromBurnConfig(parsed.burnConfig, options);
}

/** 校验 forward / reverse 坐标换算可往返（测试与调试） */
export function burnBoxRoundTripMatchesFrame(params: {
  frameRect: { boxX1: number; boxY1: number; boxX2: number; boxY2: number };
  previewBoxHeightPx: number;
  textBlockHeightPx: number;
  contentH: number;
  canvasH: number;
}): boolean {
  const burn = compensateBurnBoxForBackendTopAlign({
    frameRect: params.frameRect,
    previewBoxHeightPx: params.previewBoxHeightPx,
    textBlockHeightPx: params.textBlockHeightPx,
    contentH: params.contentH,
    canvasH: params.canvasH,
  });
  const restored = reverseBurnBoxToDisplayFrame({
    burnRect: burn,
    previewBoxHeightPx: params.previewBoxHeightPx,
    textBlockHeightPx: params.textBlockHeightPx,
    contentH: params.contentH,
    canvasH: params.canvasH,
  });
  return (
    restored.boxX1 === params.frameRect.boxX1 &&
    restored.boxY1 === params.frameRect.boxY1 &&
    restored.boxX2 === params.frameRect.boxX2 &&
    restored.boxY2 === params.frameRect.boxY2
  );
}
